/**
 * The correction queue — settlements the provider feed says the ledger got wrong.
 *
 * WHY THIS SCREEN EXISTS. The bet-settlement job WRITES a proposal when provider
 * consensus contradicts a settlement already recorded, and writing one moves no
 * money. Nothing applies it. Without this screen the queue accumulates rows that
 * describe real money sitting in the wrong place and no operator can see them.
 *
 * APPLYING MOVES MONEY IN BOTH DIRECTIONS in one transaction — the recorded
 * settlement is reversed out of the ledger and a replacement applied — which is
 * why apply is OWNER_ONLY and why REJECT is too. "The provider is wrong and the
 * original stands" is an equally accountable ruling, not a queue-clearing click.
 *
 * THE OPERATOR TYPES NOTHING BUT THE REASON. Outcome, factor, request id and
 * `expectedSettlementVersion` are all carried from the row that was clicked. The
 * version especially: it is the optimistic-concurrency guard, and a hand-typed
 * one defeats the only thing standing between an operator and a second
 * correction stacked on top of a first they never saw.
 *
 * The settle panel at the bottom is a DIFFERENT operation and is deliberately
 * not dressed as this one. It uses the raw `Modal`, not `ConfirmAction`, because
 * `POST /admin/bets/{betId}/settle` has no reason field in the contract —
 * collecting a mandatory reason the server cannot store would tell the operator
 * their account of why was recorded when it was discarded.
 */
import { useState } from 'react';
import {
  useResettlementRequests,
  useResettleBet,
  useRejectResettlement,
  useSettleBet,
} from '@/hooks/useAdmin';
import { useMe } from '@/hooks/useMe';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Pill } from '@/components/ui/StatusPill';
import { ConfirmAction, Modal, ActionError } from '@/components/ui/Modal';
import { AdminLoading, AdminError, AdminPageHeader } from './AdminAsync';
import {
  ROLE_SETS,
  isRoleAuthorized,
  validateBetId,
  validateReason,
  isPendingProposal,
  formatFactor,
  describeResettlement,
  describeResettlementRejection,
  describeSettleBet,
} from './logic';
import type { BetResettlementRequest, BetResettlementStatus } from '@/types/api';

function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <div className="text-[10px] font-semibold uppercase tracking-[0.08em] text-white/35">
        {label}
      </div>
      <div className="mt-0.5 text-sm text-white/80">{value}</div>
    </div>
  );
}

function when(iso: string | null | undefined): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-AU');
}

function timeAgo(iso: string, now: number): string {
  const secs = Math.max(0, Math.round((now - new Date(iso).getTime()) / 1000));
  if (secs < 60) return `${secs}s ago`;
  if (secs < 3600) return `${Math.round(secs / 60)}m ago`;
  if (secs < 86400) return `${Math.round(secs / 3600)}h ago`;
  return `${Math.round(secs / 86400)}d ago`;
}

/** What the server was asked to do, shown in place of the form once it has. */
function Succeeded({ children }: { children: React.ReactNode }) {
  return (
    <div className="rounded-xl border border-brand-teal/35 bg-brand-teal/[0.07] p-3">
      <p className="text-sm leading-relaxed text-white/85">{children}</p>
    </div>
  );
}

function Chip({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={`rounded-lg border px-2.5 py-1 text-xs font-medium capitalize transition ${
        active
          ? 'border-brand-teal/35 bg-brand-teal/10 text-brand-teal'
          : 'border-white/10 bg-white/[0.03] text-white/50 hover:bg-white/[0.06] hover:text-white/80'
      }`}
    >
      {children}
    </button>
  );
}

/**
 * The evidence the job acted on, behind a disclosure rather than on the card.
 * Shape varies by provider and the contract says so (`additionalProperties`),
 * so it is rendered as JSON rather than as fields that would silently drop
 * anything the renderer was not written to expect.
 */
function ProviderEvidence({ value }: { value: unknown }) {
  const [open, setOpen] = useState(false);
  if (value == null) return null;
  return (
    <div className="mt-3">
      <button
        onClick={() => setOpen((v) => !v)}
        className="text-xs font-medium text-white/45 underline-offset-2 transition hover:text-white/75 hover:underline"
      >
        {open ? 'Hide provider evidence' : 'Show provider evidence'}
      </button>
      {open && (
        <pre className="mt-2 max-h-64 overflow-auto rounded-xl border border-white/[0.08] bg-black/25 p-3 font-mono text-[11px] leading-relaxed text-white/60">
          {JSON.stringify(value, null, 2)}
        </pre>
      )}
    </div>
  );
}

const STATUSES: BetResettlementStatus[] = ['pending', 'approved', 'rejected'];

/** Which dialog is open. `null` is none — the three are mutually exclusive. */
type Action =
  | { kind: 'resettle'; row: BetResettlementRequest }
  | { kind: 'reject'; row: BetResettlementRequest }
  | { kind: 'settle' }
  | null;

type SettleOutcome = 'won' | 'lost' | 'void';
const SETTLE_OUTCOMES: SettleOutcome[] = ['won', 'lost', 'void'];

export function ResettlementPage() {
  const [status, setStatus] = useState<BetResettlementStatus | undefined>('pending');
  const query = useResettlementRequests({ status });
  const me = useMe();

  /*
    Hooks are called unconditionally, ABOVE the early returns below. A mutation
    hook behind an `if` is a different hook order on the render after the fetch
    resolves.
  */
  const resettle = useResettleBet();
  const reject = useRejectResettlement();
  const settle = useSettleBet();

  const [action, setAction] = useState<Action>(null);
  const [reason, setReason] = useState('');
  const [settleBetId, setSettleBetId] = useState('');
  const [settleOutcome, setSettleOutcome] = useState<SettleOutcome>('won');
  const [settleTouched, setSettleTouched] = useState(false);

  /*
    Closing resets the MUTATIONS, not just the form. React Query keeps the last
    result on the mutation object, so a re-opened dialog would otherwise show
    the previous action's success panel over an untouched form.
  */
  function close() {
    setAction(null);
    resettle.reset();
    reject.reset();
    settle.reset();
  }

  function openResettle(row: BetResettlementRequest) {
    setReason('');
    resettle.reset();
    reject.reset();
    setAction({ kind: 'resettle', row });
  }

  function openReject(row: BetResettlementRequest) {
    setReason('');
    resettle.reset();
    reject.reset();
    setAction({ kind: 'reject', row });
  }

  function openSettle() {
    setSettleBetId('');
    setSettleOutcome('won');
    setSettleTouched(false);
    settle.reset();
    setAction({ kind: 'settle' });
  }

  const myRole = me.data?.user.role ?? null;
  /*
    DISPLAY ONLY — the API re-checks the claim on every request and is the
    authority. Both queue writes are OWNER_ONLY; settling a bet that never
    settled is ANY_ADMIN, and they are gated separately because they are
    different decisions with different blast radii.
  */
  const canRule = isRoleAuthorized(myRole, ROLE_SETS.OWNER_ONLY);
  const canSettle = isRoleAuthorized(myRole, ROLE_SETS.ANY_ADMIN);

  const settleIdError = validateBetId(settleBetId);
  const reasonError = validateReason(reason);

  const filters = (
    <div className="mb-4 flex flex-wrap items-center gap-1.5">
      <span className="mr-1 text-[10px] font-semibold uppercase tracking-[0.08em] text-white/30">
        Status
      </span>
      {STATUSES.map((s) => (
        <Chip key={s} active={status === s} onClick={() => setStatus(s)}>
          {s}
        </Chip>
      ))}
      <Chip active={status === undefined} onClick={() => setStatus(undefined)}>
        all
      </Chip>
    </div>
  );

  return (
    <>
      <AdminPageHeader
        title="Corrections"
        subtitle="Settlements the provider feed contradicts. A proposal on this queue has moved no money; applying one reverses the recorded settlement out of the ledger and applies a replacement in the same transaction."
        actions={
          canSettle ? (
            <Button variant="secondary" size="sm" onClick={openSettle}>
              Settle a bet
            </Button>
          ) : undefined
        }
      />

      {filters}

      {query.isLoading ? (
        <AdminLoading label="Reading the correction queue" />
      ) : query.isError || !query.data ? (
        <AdminError error={query.error} onRetry={() => void query.refetch()} />
      ) : query.data.items.length === 0 ? (
        <Card>
          <p className="text-sm leading-relaxed text-white/55">
            No proposal matches this filter. That is an answer, not an error — the read
            succeeded. A pending queue that is empty means provider consensus agrees with every
            settlement the ledger has recorded.
          </p>
        </Card>
      ) : (
        <div className="space-y-3">
          {query.data.items.map((row) => {
            /* Age is relative to the fetch, not to render — see MarketVisibilityPage. */
            const now = query.dataUpdatedAt;
            const pending = isPendingProposal(row.status);
            const factor = formatFactor(row.proposedFactor);

            return (
              <Card key={row.id}>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-display text-sm font-bold tracking-tight">
                        {row.currentStatus} → {row.proposedOutcome}
                        {factor ? ` at ${factor}` : ''}
                      </span>
                      <Pill tone={pending ? 'caution' : 'muted'}>{row.status}</Pill>
                    </div>
                    <p className="mt-1 font-mono text-[11px] text-white/35">{row.betId}</p>
                    <p className="mt-2 text-sm leading-relaxed text-white/70">{row.reason}</p>
                  </div>

                  {canRule && pending && (
                    <div className="flex items-center gap-2">
                      <Button variant="secondary" size="sm" onClick={() => openReject(row)}>
                        Reject
                      </Button>
                      <Button size="sm" onClick={() => openResettle(row)}>
                        Apply correction
                      </Button>
                    </div>
                  )}
                </div>

                <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
                  <Field label="Recorded outcome" value={row.currentStatus} />
                  <Field label="Proposed outcome" value={row.proposedOutcome} />
                  <Field label="Proposed factor" value={String(row.proposedFactor)} />
                  <Field
                    label="Settlement version"
                    value={<span className="font-mono text-xs">{row.currentSettlementVersion}</span>}
                  />
                  <Field
                    label="Proposed by"
                    value={<span className="font-mono text-xs">{row.proposedBy}</span>}
                  />
                  <Field
                    label="Proposed"
                    value={`${timeAgo(row.createdAt, now)} · ${when(row.createdAt)}`}
                  />
                  {!pending && (
                    <>
                      <Field
                        label="Resolved"
                        value={row.resolvedAt ? `${timeAgo(row.resolvedAt, now)} · ${when(row.resolvedAt)}` : '—'}
                      />
                      <Field
                        label="Resolved by"
                        value={
                          row.resolvedByUserId ? (
                            <span className="font-mono text-xs">{row.resolvedByUserId}</span>
                          ) : (
                            '—'
                          )
                        }
                      />
                    </>
                  )}
                </div>

                {!pending && row.resolutionReason && (
                  <div className="mt-3">
                    <Field label="Resolution reason" value={row.resolutionReason} />
                  </div>
                )}

                <ProviderEvidence value={row.providerResult} />

                {!pending && (
                  <p className="mt-3 rounded-xl border border-white/[0.08] bg-white/[0.03] p-3 text-xs leading-relaxed text-white/50">
                    This proposal has been ruled on. There is nothing further to press — a second
                    correction is proposed as its own row, against the settlement version this one
                    produced.
                  </p>
                )}
              </Card>
            );
          })}
        </div>
      )}

      <ConfirmAction
        open={action?.kind === 'resettle'}
        title="Apply this correction"
        confirmLine={action?.kind === 'resettle' ? describeResettlement(action.row) : ''}
        confirmLabel="Apply correction"
        danger
        pending={resettle.isPending}
        error={resettle.error}
        reason={reason}
        onReasonChange={setReason}
        reasonError={reasonError}
        onClose={close}
        /*
          Everything but the reason is CARRIED from the clicked row. The
          settlement version in particular: quoting it back is what makes a bet
          that moved since this queue was read refuse with `version_mismatch`
          instead of stacking a second correction on a first nobody saw.
        */
        onConfirm={(note) => {
          if (action?.kind !== 'resettle') return;
          // 0049: a multi proposal that REOPENS the ticket carries
          // proposedOutcome 'pending'. The unchanged v3 apply body has no
          // 'pending' value, so by the handoff convention the corrected leg's
          // own outcome is sent and the server derives the ticket state from
          // the stored proposal (requestId). Never an operator-typed value.
          const outcome =
            action.row.proposedOutcome === 'pending'
              ? (action.row.proposedLegs?.[0]?.proposedOutcome ?? 'void')
              : action.row.proposedOutcome;
          resettle.mutate({
            betId: action.row.betId,
            body: {
              outcome,
              factor: action.row.proposedFactor,
              reason: note,
              expectedSettlementVersion: action.row.currentSettlementVersion,
              requestId: action.row.id,
            },
          });
        }}
        success={
          resettle.isSuccess && action?.kind === 'resettle' ? (
            <Succeeded>
              The correction is applied to {action.row.betId}. The recorded settlement was
              reversed out of the ledger and the replacement applied in one transaction; the
              placement stake and the 4% fee were not reversed. The proposal is closed and the
              queue has been re-read.
            </Succeeded>
          ) : undefined
        }
      />

      <ConfirmAction
        open={action?.kind === 'reject'}
        title="Reject this correction"
        confirmLine={action?.kind === 'reject' ? describeResettlementRejection(action.row) : ''}
        confirmLabel="Reject correction"
        pending={reject.isPending}
        error={reject.error}
        reason={reason}
        onReasonChange={setReason}
        reasonError={reasonError}
        onClose={close}
        onConfirm={(note) => {
          if (action?.kind !== 'reject') return;
          reject.mutate({ id: action.row.id, body: { reason: note } });
        }}
        success={
          reject.isSuccess && action?.kind === 'reject' ? (
            <Succeeded>
              The proposal is closed as rejected. No money moved and the recorded{' '}
              {action.row.currentStatus} stands. Your ruling is recorded against your account.
            </Succeeded>
          ) : undefined
        }
      />

      {/*
        Deliberately the raw Modal. `POST /admin/bets/{betId}/settle` takes an
        outcome and nothing else — there is no reason field in the contract — so
        `ConfirmAction` would collect an account of why and discard it.
      */}
      <Modal
        open={action?.kind === 'settle'}
        title="Settle a bet that never settled"
        onClose={close}
        footer={
          settle.isSuccess ? (
            <Button variant="secondary" size="sm" onClick={close}>
              Close
            </Button>
          ) : (
            <>
              <Button variant="secondary" size="sm" onClick={close} disabled={settle.isPending}>
                Cancel
              </Button>
              <Button
                variant={settleOutcome === 'void' ? 'danger' : 'primary'}
                size="sm"
                loading={settle.isPending}
                onClick={() => {
                  setSettleTouched(true);
                  if (settleIdError) return;
                  settle.mutate({
                    betId: settleBetId.trim(),
                    body: { outcome: settleOutcome },
                  });
                }}
              >
                {settleOutcome === 'void' ? 'Void bet' : 'Settle bet'}
              </Button>
            </>
          )
        }
      >
        {settle.isSuccess ? (
          <Succeeded>
            {settleBetId.trim()} is recorded as {settleOutcome}. This endpoint is idempotent on
            bet status — if the bet had already settled, the existing state was returned and no
            ledger events were emitted.
          </Succeeded>
        ) : (
          <>
            <p className="rounded-xl border border-white/[0.08] bg-white/[0.03] p-3 text-sm leading-relaxed text-white/85">
              {settleIdError
                ? 'Enter the bet id to settle. Nothing is reversed here — this is for a bet that is still pending, not a correction to one that already settled.'
                : describeSettleBet(settleBetId.trim(), settleOutcome)}
            </p>

            <div className="mt-4 space-y-3">
              <div>
                <label htmlFor="settle-bet-id" className="text-sm font-medium text-white/80">
                  Bet id
                </label>
                <input
                  id="settle-bet-id"
                  value={settleBetId}
                  onChange={(e) => setSettleBetId(e.target.value)}
                  placeholder="00000000-0000-0000-0000-000000000000"
                  spellCheck={false}
                  autoComplete="off"
                  className={`mt-1 w-full rounded-lg border bg-brand-surface px-3 py-2 font-mono text-sm text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-brand-teal ${
                    settleTouched && settleIdError ? 'border-red-500' : 'border-white/20'
                  }`}
                />
                {settleTouched && settleIdError ? (
                  <p className="mt-1 text-xs text-red-400">{settleIdError}</p>
                ) : (
                  <p className="mt-1 text-xs text-white/35">
                    Copied from the bet row on the user&rsquo;s account.
                  </p>
                )}
              </div>

              <div>
                <span className="text-sm font-medium text-white/80">Outcome</span>
                <div className="mt-1.5 flex flex-wrap gap-1.5">
                  {SETTLE_OUTCOMES.map((o) => (
                    <Chip
                      key={o}
                      active={settleOutcome === o}
                      onClick={() => setSettleOutcome(o)}
                    >
                      {o}
                    </Chip>
                  ))}
                </div>
              </div>
            </div>

            <p className="mt-4 text-xs leading-relaxed text-white/35">
              This endpoint records no reason, so nothing you could write here would be kept.
              The settlement is recorded against the bet, and the event carries your account as
              the caller.
            </p>

            {settle.error != null && <ActionError error={settle.error} />}
          </>
        )}
      </Modal>
    </>
  );
}
